import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QrCode, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * DocketQR — click-to-open modal that renders a large QR code linking to the
 * public docket page (`/d/:token`). The driver can hold the tablet up to the
 * customer so they can scan without needing the printed docket.
 *
 * Props:
 *   docket     — { qr_token, docket_no, customer_name, mix_code, quantity_m3, site_address }
 *   size       — "sm" | "lg" (button style)
 *   label      — optional button label (default "QR")
 */
export function DocketQR({ docket, size = "sm", label = "QR" }) {
  const [open, setOpen] = useState(false);
  const token = docket?.qr_token;
  if (!token) return null;
  const url = `${window.location.origin}/d/${token}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const btnClass = size === "lg"
    ? "inline-flex items-center gap-1.5 border border-industrial px-3 py-1.5 uppercase tracking-widest text-xs font-bold hover:border-primary-orange hover:text-primary-orange"
    : "text-primary-orange hover:underline inline-flex items-center gap-1 text-xs uppercase mono";

  return (
    <>
      <button
        type="button"
        data-testid={`docket-qr-btn-${docket.docket_no}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={btnClass}
        title="Show scannable QR code"
      >
        <QrCode className="w-3.5 h-3.5" /> {label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-md" data-testid={`docket-qr-modal-${docket.docket_no}`}>
          <DialogHeader>
            <DialogTitle className="heading uppercase flex items-center gap-2">
              Scan Docket <span className="mono text-primary-orange">{docket.docket_no}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs mono text-muted-industrial leading-relaxed">
              Hand the tablet to the customer or hold it up so they can scan and
              view a live copy of this docket on their phone.
            </div>
            <div className="flex justify-center bg-white p-6 border border-industrial">
              <QRCodeSVG
                value={url}
                size={240}
                level="M"
                includeMargin={false}
                data-testid={`docket-qr-svg-${docket.docket_no}`}
              />
            </div>
            <div className="space-y-1.5 text-[11px] mono text-muted-industrial">
              <div><span className="label-tag mr-2">Customer</span>{docket.customer_name || "—"}</div>
              {docket.site_address && <div><span className="label-tag mr-2">Site</span>{docket.site_address}</div>}
              <div>
                <span className="label-tag mr-2">Load</span>
                {docket.quantity_m3} m³ · {docket.mix_code}
              </div>
            </div>
            <div className="flex items-center gap-2 border border-industrial p-2 bg-surface-2">
              <div className="mono text-[10px] text-muted-industrial truncate flex-1" title={url}>{url}</div>
              <button
                type="button"
                onClick={copy}
                data-testid={`docket-qr-copy-${docket.docket_no}`}
                className="text-primary-orange hover:underline inline-flex items-center gap-1 text-[10px] uppercase mono shrink-0"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                data-testid={`docket-qr-open-${docket.docket_no}`}
                className="text-primary-orange hover:underline inline-flex items-center gap-1 text-[10px] uppercase mono shrink-0"
              >
                <ExternalLink className="w-3 h-3" /> Open
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
